export const AssetsListVisibility = {
  Private: "private",
  /** Anyone on the show */
  Public: "public",
} as const;

export type AssetsListVisibility =
  (typeof AssetsListVisibility)[keyof typeof AssetsListVisibility];

export const ASSETS_LIST_VISIBILITIES: AssetsListVisibility[] =
  Object.values(AssetsListVisibility);

export const DEFAULT_ASSETS_LIST_VISIBILITY: AssetsListVisibility =
  AssetsListVisibility.Public;

export const isAssetsListVisibility = (
  value: unknown,
): value is AssetsListVisibility =>
  typeof value === "string" &&
  (ASSETS_LIST_VISIBILITIES as string[]).includes(value);

export const MAX_ASSETS_LIST_NAME_LENGTH = 120;

/**
 * The measured properties of the source media, captured by the player when the
 * entry is added. An edit decision list needs a frame rate and a frame size
 * before it can express anything in frames, and neither is stored on the Asset
 * — only the player that decoded the file knows them.
 *
 * Every field is nullable: an image has no frame rate, and a video whose
 * metadata never loaded has nothing to report. The exporter supplies its own
 * fallbacks rather than trusting these to be present.
 */
export interface AssetsListEntrySourceMedia {
  /** Frames per second, as detected during playback. */
  source_frame_rate: number | null;
  /** Full length of the source file in seconds, before any trim is applied. */
  source_duration: number | null;
  source_width: number | null;
  source_height: number | null;
}

/** Trim points belong to the entry, not the asset — the same asset may appear more than once. */
export interface AssetsListEntry extends AssetsListEntrySourceMedia {
  asset_id: string;
  in_time: number | null;
  out_time: number | null;
}

export interface PopulatedAssetsListEntry extends AssetsListEntrySourceMedia {
  asset_id: AssetsListAsset;
  in_time: number | null;
  out_time: number | null;
}

/** Field names of {@link AssetsListEntrySourceMedia}, for normalisation loops. */
export const ASSETS_LIST_ENTRY_SOURCE_MEDIA_FIELDS = [
  "source_frame_rate",
  "source_duration",
  "source_width",
  "source_height",
] as const;

export type AssetsListEntrySourceMediaField =
  (typeof ASSETS_LIST_ENTRY_SOURCE_MEDIA_FIELDS)[number];

export interface AssetsListAsset {
  _id: string;
  name: string;
  mediaType: "image" | "video" | "audio" | "file" | "none";
  url: string;
  thumbnail_url: string;
  /** Omitted entirely, not null, on assets with no duration (images, files). */
  duration?: number | null;
  archived: boolean;
}

/**
 * Audio has no visual frame to show in the strip and nothing to trim on a
 * timeline, so it is the one media type an assets list can never contain.
 * A single source of truth here is what lets the frontend block the drag/ADD
 * path with the same rule the api enforces server-side.
 */
export const BLOCKED_ASSETS_LIST_MEDIA_TYPES: AssetsListAsset["mediaType"][] = [
  "audio",
];

export const isAssetsListMediaTypeAllowed = (mediaType: unknown): boolean =>
  !(BLOCKED_ASSETS_LIST_MEDIA_TYPES as unknown[]).includes(mediaType);

export interface AssetsListOwner {
  id: string;
  email: string;
}

export interface AssetsListSummary {
  _id: string;
  name: string;
  visibility: AssetsListVisibility;
  user: AssetsListOwner | null;
  assetCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AssetsListDetail {
  _id: string;
  name: string;
  visibility: AssetsListVisibility;
  user: AssetsListOwner | null;
  show: string;
  account: string;
  assets: PopulatedAssetsListEntry[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateAssetsListRequest {
  name: string;
  visibility?: AssetsListVisibility;
  assets?: AssetsListEntry[];
}

export interface UpdateAssetsListRequest {
  name?: string;
  visibility?: AssetsListVisibility;
  assets?: AssetsListEntry[];
}
